import { useMemo } from "react";
import { evaluateVisibility } from "../../shared/visibility/evaluator";
import { extractDataSources } from "../../shared/visibility/extractDataSources";
import { useVisibilityContext } from "./useVisibilityContext";
import type { Page } from "../../shared/schema";
import type { DataSourceType } from "../../shared/schema/visibility";

export interface UseVisiblePagesResult {
  visiblePages: Page[];
  isLoading: boolean;
}

export const useVisiblePages = (pages: Page[]): UseVisiblePagesResult => {
  const requiredSources = useMemo(() => {
    const sources = new Set<DataSourceType>();
    for (const page of pages) {
      if (page.visibility?.condition) {
        extractDataSources(page.visibility.condition).forEach((s) => sources.add(s));
      }
    }
    return sources;
  }, [pages]);

  const { context, isLoading } = useVisibilityContext({ requiredSources });

  const visiblePages = useMemo(() => {
    return pages.filter((page) => {
      if (!page.visibility?.condition) return true;
      // Show all pages while loading to prevent navigation from collapsing
      if (isLoading || !context) return true;
      return evaluateVisibility(page.visibility.condition, context);
    });
  }, [pages, context, isLoading]);

  return { visiblePages, isLoading };
};
